import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser, SocialProviderId } from '@/lib/auth';
import * as authLib from '@/lib/auth';
import api from '@/lib/api';

interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isInitialized: boolean;

  initialize: () => Promise<void>;
  login: (email: string, password: string, turnstileToken?: string) => Promise<void>;
  register: (email: string, password: string, name?: string, turnstileToken?: string, referralCode?: string) => Promise<void>;
  /** Preferred entry point — the login/signup pages render providers dynamically. */
  loginWithProvider: (provider: SocialProviderId) => Promise<void>;
  loginWithGoogle: () => Promise<void>;
  loginWithApple: () => Promise<void>;
  loginWithFacebook: () => Promise<void>;
  handleCallback: () => Promise<void>;
  logout: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  startPreview: () => Promise<void>;
  exitPreview: () => Promise<void>;
}

/**
 * Load `/user/profile`, retrying a transient failure before giving up.
 *
 * `initialize()` used to take a single failed fetch as proof the visitor is not
 * an admin: the catch set `user: null, isInitialized: true`, which is a
 * TERMINAL answer, and MaintenanceGuard reads exactly that to decide whether to
 * show the maintenance page. One blip therefore locked a platform owner onto
 * "Coming Soon" until they reloaded by hand (volarly.com, 2026-08-19 — the
 * server was correct throughout: role admin, is_platform_admin true, bypass
 * granted).
 *
 * The window this matters most is right after an OAuth callback, where the
 * first profile call races session establishment — which is why it looks
 * tenant-specific when it is really just latency.
 */
const PROFILE_RETRIES = 3;
const PROFILE_RETRY_DELAY_MS = 400;

async function fetchProfileWithRetry(): Promise<any> {
  let lastError: unknown;
  for (let attempt = 0; attempt < PROFILE_RETRIES; attempt++) {
    try {
      return await api.get<any>('/user/profile');
    } catch (err) {
      lastError = err;
      if (attempt < PROFILE_RETRIES - 1) {
        await new Promise((r) => setTimeout(r, PROFILE_RETRY_DELAY_MS * (attempt + 1)));
      }
    }
  }
  throw lastError;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: null,
      isLoading: false,
      isInitialized: false,

      initialize: async () => {
        try {
          set({ isLoading: true });
          const authenticated = await authLib.isAuthenticated();
          if (authenticated) {
            const data = await fetchProfileWithRetry();
            const user: AuthUser = {
              id: data.id || data.user_id,
              email: data.email || '',
              firstName: data.first_name || '',
              lastName: data.last_name || '',
              role: data.role || 'user',
              capabilities: Array.isArray(data.capabilities) ? data.capabilities : [],
              isPlatformAdmin: data.is_platform_admin || false,
              avatarUrl: data.avatar_url || null,
              hasActiveSubscription: !!data.has_active_subscription,
              activePlanSlug: data.active_plan_slug ?? null,
              previewActive: !!data.preview?.active,
              previewExpiresAt: data.preview?.expiresAt ?? null,
            };
            set({ user, isInitialized: true, isLoading: false });
            return;
          }
          set({ user: null, isInitialized: true, isLoading: false });
        } catch {
          set({ user: null, isInitialized: true, isLoading: false });
        }
      },

      login: async (email, password, turnstileToken) => {
        set({ isLoading: true });
        try {
          const { user } = await authLib.signIn(email, password, turnstileToken);
          set({ user, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      register: async (email, password, name, turnstileToken, referralCode) => {
        set({ isLoading: true });
        try {
          const { user } = await authLib.signUp(email, password, name, turnstileToken, referralCode);
          set({ user, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      loginWithProvider: async (provider) => {
        await authLib.signInWithProvider(provider);
      },

      loginWithGoogle: async () => {
        await authLib.signInWithGoogle();
      },

      loginWithApple: async () => {
        await authLib.signInWithApple();
      },

      loginWithFacebook: async () => {
        await authLib.signInWithFacebook();
      },

      handleCallback: async () => {
        set({ isLoading: true });
        try {
          const { user } = await authLib.handleAuthCallback();
          set({ user, isLoading: false });
        } catch (error) {
          set({ isLoading: false });
          throw error;
        }
      },

      logout: async () => {
        await authLib.signOut();
        // Belt-and-suspenders: clear any remaining SuperTokens tokens from localStorage
        // in case signOut() didn't fully clear them (prevents stale session on refresh)
        for (const key of Object.keys(localStorage)) {
          if (key.startsWith('st-') || key.startsWith('supertokens')) {
            localStorage.removeItem(key);
          }
        }
        set({ user: null });
      },

      setUser: (user) => set({ user }),

      // "View as customer": flip the session to a regular-user view, then
      // re-fetch the (now customer-shaped) profile so all gating updates.
      startPreview: async () => {
        const { startPreview } = await import('@/lib/preview');
        await startPreview();
        await useAuthStore.getState().initialize();
      },
      exitPreview: async () => {
        const { exitPreview } = await import('@/lib/preview');
        await exitPreview();
        await useAuthStore.getState().initialize();
      },
    }),
    {
      name: 'rumio-auth',
      // v3 (2026-05-07): added hasActiveSubscription/activePlanSlug to AuthUser.
      // Bumping version clears the persisted user so initialize() re-fetches
      // a fresh profile with the new fields populated.
      version: 3,
      partialize: (state) => ({ user: state.user }),
      migrate: () => ({ user: null }),
    }
  )
);
